import { useEffect, useMemo, useState } from 'react';
import {
  api,
  type ConnectionSummary,
  type ConnectorManifestSummary,
  type FactorySummary,
} from '../api.ts';
import { IdentityBadge, botLabelFor, identityTip } from '../components/IdentityBadge.tsx';
import { useSidebarPane } from '../components/PaneResizer.tsx';
import { pathFor } from '../routes.ts';

/**
 * Which connections are yours to connect, which are the workspace's, and
 * whether each one works.
 *
 * A connection is a pointer, not a grant — the credential is resolved per call
 * from whoever is acting. The rail is split by *whose* credential that is,
 * because it decides who can fix a broken one: a personal account is linked by
 * the person reading, a workspace service by an admin with the key. The old
 * rail grouped by readiness instead, and a new reader could not tell, from
 * "Needs you" and "Needs an admin", that the difference was the kind of
 * credential rather than a queue somebody had assigned.
 *
 * The pane shows the one thing to press per credential and folds everything a
 * person reads once — endpoint, hosts, policy, the tool catalog — under
 * "Details". Narrowing what a connection may reach is not done here at all: a
 * grant written at registration is a guess about workflows not yet written, so
 * the scope lives on the mount in the builder, where the question has an
 * answer and the resources can be ticked rather than typed.
 */
export function ConnectionsView({
  connections,
  factories,
  connectionId,
  isAdmin,
  onRefresh,
  onOpenConnection,
  onOpenWorkflow,
}: {
  connections: ConnectionSummary[];
  /** The shell's list, so each connection can say which factory stages mount it. */
  factories: FactorySummary[];
  /**
   * From the path. Null when the screen was reached from the rail, and the
   * first connection is shown — derived here rather than written back, so the
   * shell's list arriving does not overwrite a choice already made.
   */
  connectionId: string | null;
  isAdmin: boolean;
  onRefresh(): void;
  onOpenConnection(id: string): void;
  onOpenWorkflow(id: string): void;
}) {
  const [adding, setAdding] = useState(false);

  /*
   * Which connection is open belongs to the URL, not to this screen:
   * `/connections/:id?linked=1` is where an OAuth handshake returns, and a
   * connection with no address landed whoever had just connected Linear on
   * whichever row sorts first.
   */
  const selected = useMemo(
    () => connections.find((entry) => entry.id === connectionId) ?? connections[0] ?? null,
    [connections, connectionId],
  );

  const groups = useMemo(() => groupByOwner(connections), [connections]);
  const needsYou = connections.filter(needsYourLink);
  const drifted = connections.filter((entry) => entry.catalogDrifted);

  const pane = useSidebarPane('reef.connections.sidebar', 232);

  return (
    <div {...pane.body}>
      <aside className="sidebar">
        <p className="section-title">Connections</p>
        {connections.length === 0 && <p className="hint">No connections registered yet.</p>}
        {connections.length > 0 &&
          groups.map((group) => (
            <section className="sidebar-group" data-group={group.key} key={group.key}>
              <h3 className="sidebar-group-title">
                {group.title}
                <span className="sidebar-group-count">{group.connections.length}</span>
              </h3>
              <p className="hint sidebar-group-hint">{group.hint}</p>
              {group.connections.map((connection) => (
                <button
                  key={connection.id}
                  className="list-item"
                  aria-current={connection.id === selected?.id && !adding}
                  onClick={() => {
                    setAdding(false);
                    onOpenConnection(connection.id);
                  }}
                >
                  {connection.name}
                  <small className="row wrap connection-row-badges">
                    <StatusBadges connection={connection} />
                  </small>
                </button>
              ))}
            </section>
          ))}

        {isAdmin && (
          <button
            className="list-item"
            aria-current={adding}
            style={{ marginTop: '0.75rem' }}
            onClick={() => setAdding(true)}
          >
            + Add a connection
          </button>
        )}
      </aside>

      {pane.resizer}

      <main className="main">
        {needsYou.length > 0 && !adding && (
          <div className="banner warn">
            <h3>
              {needsYou.length} account{needsYou.length === 1 ? '' : 's'} waiting on you
            </h3>
            <p style={{ margin: '0 0 0.75rem' }}>
              Steps that run as you stop until these are connected. Steps that run as a bot are
              unaffected.
            </p>
            <div className="row wrap">
              {needsYou.map((connection) => (
                <a key={connection.id} className="button primary" href={connection.connectUrl ?? '#'}>
                  Connect {connection.name}
                </a>
              ))}
            </div>
          </div>
        )}

        {drifted.length > 0 && !adding && (
          <div className="banner warn">
            <h3>
              {drifted.length} connection{drifted.length === 1 ? '' : 's'} changed since approval
            </h3>
            <p style={{ margin: 0 }}>
              {drifted.map((entry) => entry.name).join(', ')}: the tools on offer differ from the
              ones approved. Steps keep using the approved set until an admin approves the change
              under Details.
            </p>
          </div>
        )}

        {adding && <AddConnection onDone={() => { setAdding(false); onRefresh(); }} />}

        {!adding && !selected && <div className="empty">No connections registered yet.</div>}

        {!adding && selected && (
          <ConnectionDetail
            connection={selected}
            factories={factories}
            isAdmin={isAdmin}
            onRefresh={onRefresh}
            onOpenWorkflow={onOpenWorkflow}
          />
        )}
      </main>
    </div>
  );
}

/**
 * One badge per credential the connection holds, saying whether it works.
 *
 * The glyph says whose credential it is and the word says its state, so a
 * dual-identity row reads as two facts rather than a sentence to parse.
 */
function StatusBadges({ connection }: { connection: ConnectionSummary }) {
  if (!connection.usesUser && !connection.usesBot) return <>Reef internal</>;
  return (
    <>
      {connection.usesUser && (
        <IdentityBadge
          kind="person"
          label={connection.userLinked ? 'Connected' : 'Not connected'}
        />
      )}
      {connection.usesBot && (
        <IdentityBadge kind="bot" label={connection.botReady ? 'Ready' : 'Not set up'} />
      )}
    </>
  );
}

function ConnectionDetail({
  connection,
  factories,
  isAdmin,
  onRefresh,
  onOpenWorkflow,
}: {
  connection: ConnectionSummary;
  factories: FactorySummary[];
  isAdmin: boolean;
  onRefresh(): void;
  onOpenWorkflow(id: string): void;
}) {
  const [busy, setBusy] = useState<string | null>(null);
  const [note, setNote] = useState<{ from: string; about: string; text: string } | null>(null);

  /*
   * The outcome is reported where the button was pressed, and carries which
   * connection it is *about*: this pane is not remounted when the selection
   * changes, so a re-read still in flight lands after somebody has moved on,
   * and "12 tools" shown under a different connection is not stale, it is
   * false.
   */
  const run = async (label: string, work: () => Promise<string>) => {
    const about = connection.id;
    setBusy(label);
    setNote(null);
    try {
      const text = await work();
      setNote({ from: label, about, text });
      onRefresh();
    } catch (error) {
      setNote({ from: label, about, text: (error as Error).message });
    } finally {
      setBusy(null);
    }
  };

  /*
   * Never discovered, as opposed to discovered and empty. The approval on file
   * tells the two apart, and only the first is a state where approving would
   * mean nothing.
   */
  const nothingEverDiscovered = connection.toolCount === 0 && !connection.catalogFingerprint;

  useEffect(() => setNote(null), [connection.id]);

  const noteFrom = (...labels: string[]) =>
    note && note.about === connection.id && labels.includes(note.from) ? (
      <div className="banner">
        <p style={{ margin: 0 }}>{note.text}</p>
      </div>
    ) : null;

  const bot = botLabelFor(connection);

  return (
    <>
      <h1 className="connection-title">{connection.name}</h1>
      <p className="hint" style={{ marginTop: 0 }}>
        {describeOwner(connection)}
        {connection.trustTier === 'byo' && <span className="pill warn"> unvetted</span>}
      </p>

      <FactoryUsage connection={connection} factories={factories} />

      {noteFrom('revoke')}

      <div className="card">
        <h2>Credentials</h2>
        <div className="stack">
          {connection.usesUser && (
            <IdentityRow
              kind="person"
              title="Your account"
              tip={identityTip('person', bot)}
              ready={connection.userLinked}
              readyText="Connected. Steps that run as you use your own access."
              blockedText={`Not connected. Steps that run as you stop here${
                connection.usesBot ? '; steps that run as the bot are unaffected' : ''
              }.`}
              action={
                connection.connectUrl ? (
                  <a className="button primary" href={connection.connectUrl}>
                    {connection.userLinked ? 'Reconnect' : 'Connect'} {connection.name}
                  </a>
                ) : null
              }
              /*
                Disconnecting is only offered for a link Reef itself holds. A
                better-auth provider is unlinked where better-auth manages it,
                and a button here that quietly did nothing would be worse than
                no button.
              */
              extra={
                connection.canRevoke ? (
                  <button
                    disabled={busy !== null}
                    onClick={() =>
                      run('revoke', async () => {
                        const result = await api.revokeConnection(connection.id);
                        return result.revokedUpstream
                          ? 'Disconnected, and the provider was told to forget the token.'
                          : 'Disconnected here. The provider offers no revocation endpoint, so the token will expire on its own.';
                      })
                    }
                  >
                    Disconnect
                  </button>
                ) : null
              }
            />
          )}

          {connection.usesBot && (
            <IdentityRow
              kind="bot"
              title={connection.serviceAccountLabel ?? 'Workspace service'}
              tip={identityTip('bot', bot)}
              ready={connection.botReady}
              readyText="Set up. Scheduled and webhook runs use this."
              blockedText={
                connection.secretName
                  ? 'Not set up. Scheduled and webhook runs stop here.'
                  : 'Not set up. A workspace admin needs to set this up.'
              }
              action={null}
              extra={
                !connection.botReady && connection.secretName ? (
                  <CredentialNeeded secretName={connection.secretName} isAdmin={isAdmin} />
                ) : null
              }
            />
          )}

          {!connection.usesUser && !connection.usesBot && (
            <p className="hint" style={{ margin: 0 }}>
              Reef's own credentials. Never handed to a step.
            </p>
          )}
        </div>
      </div>

      <div className="card">
        <h2>Used by</h2>
        {/*
          Anchors rather than buttons — `/workflows/:id` is a real address, so a
          middle-click opens the workflow *beside* the connection rather than
          over it.
        */}
        {connection.usedBy.length === 0 ? (
          <p className="hint" style={{ margin: 0 }}>
            No workflow you can see uses this connection.
          </p>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Workflow</th>
                <th>Runs as</th>
              </tr>
            </thead>
            <tbody>
              {connection.usedBy.map((workflow) => (
                <tr key={workflow.id}>
                  <td>
                    <a
                      className="linkish"
                      href={pathFor({
                        tab: 'build',
                        section: null,
                        workflowId: workflow.id,
                        runId: null,
                        connectionId: null,
                        factoryId: null,
                        item: null,
                        stepId: null,
                      })}
                      onClick={(event) => {
                        // A modified click stays the browser's: "open this
                        // somewhere else" must not navigate this tab.
                        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
                        event.preventDefault();
                        onOpenWorkflow(workflow.id);
                      }}
                    >
                      {workflow.name}
                    </a>
                  </td>
                  <td>
                    <span className="row wrap">
                      {workflow.actors.map((actor) => {
                        const kind =
                          actor === 'user' ? 'person' : actor === 'service' ? 'bot' : 'auto';
                        return (
                          <IdentityBadge
                            key={actor}
                            kind={kind}
                            label={actorLabel(actor, connection)}
                            tip={identityTip(kind, bot)}
                          />
                        );
                      })}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/*
        Folded, and last. Everything here is read once — when deciding to mount
        the connection, or by an admin approving a catalog — and open it was the
        largest thing on the pane for the least-asked question.
      */}
      <details className="card connection-details">
        <summary>Details</summary>
        <table>
          <tbody>
            <tr>
              <th style={{ width: '11rem' }}>Id</th>
              <td>
                <code>{connection.id}</code> · {connection.connector.name}
              </td>
            </tr>
            <tr>
              <th>MCP endpoint</th>
              <td>
                <code>{connection.mcpUrl}</code> ({connection.transport})
              </td>
            </tr>
            <tr>
              <th>Network</th>
              <td>
                {connection.egressHosts.length
                  ? connection.egressHosts.map((host) => (
                      <span className="pill" key={host}>
                        {host}
                      </span>
                    ))
                  : 'No extra hosts.'}
              </td>
            </tr>
            <tr>
              <th>Default policy</th>
              <td>
                {connection.defaultPolicyId ? (
                  <span className="pill accent">{connection.defaultPolicyId}</span>
                ) : (
                  'Inherits the step’s own.'
                )}
              </td>
            </tr>
            <tr>
              <th>Writes</th>
              <td>
                {connection.stagesWrites
                  ? 'Queued for approval. A human applies each write.'
                  : 'Performed directly. This connector is vetted.'}
              </td>
            </tr>
            <tr>
              <th>Tools</th>
              <td className="connection-tools">
                {/*
                  What is cached and what was approved are two facts, and the
                  second is only worth stating once there is a catalog it could
                  describe: "approved as they stood" over an empty cache
                  describes an approval of nothing as though it were a review.
                */}
                <p style={{ margin: 0 }}>
                  {connection.toolCount > 0 ? (
                    <>
                      {connection.toolCount} tool{connection.toolCount === 1 ? '' : 's'} cached.
                      {connection.catalogFingerprint ? ' Approved as they stood.' : ' Not approved yet.'}
                    </>
                  ) : (
                    <>
                      None cached.{' '}
                      {connection.catalogFingerprint
                        ? 'The tools that were approved are gone — approving records that.'
                        : 'Not approved yet — discovery runs when the connection is authorized.'}
                    </>
                  )}
                </p>

                {connection.catalogDrifted && (
                  <div className="banner warn">
                    <p style={{ margin: 0 }}>
                      {connection.toolCount === 0
                        ? 'This server now offers nothing at all; the approval on file describes tools that are gone.'
                        : 'What this server offers no longer matches what was approved. Look at the change before approving it.'}
                    </p>
                  </div>
                )}

                {isAdmin && (
                  <div className="row wrap">
                    <button
                      disabled={busy !== null}
                      onClick={() =>
                        run('refresh', async () => {
                          const result = await api.refreshConnectionTools(connection.id);
                          return `${result.tools.length} tools. ${result.driftSummary}${
                            result.needsApproval ? ' An admin needs to approve the new catalog.' : ''
                          }`;
                        })
                      }
                    >
                      Re-read tools
                    </button>
                    <button
                      /*
                       * Unavailable only when nothing has *ever* been discovered,
                       * which is the one case the control plane refuses. An
                       * emptied catalog keeps the button: approving is the only
                       * thing that clears the drift warning above.
                       */
                      disabled={busy !== null || nothingEverDiscovered}
                      title={
                        nothingEverDiscovered
                          ? 'Nothing has been discovered to approve. Re-read the tools first.'
                          : undefined
                      }
                      onClick={() =>
                        run('approve', async () => {
                          const result = await api.approveCatalog(connection.id);
                          return result.toolCount === 0
                            ? 'Recorded that this server now offers no tools.'
                            : `Approved ${result.toolCount} tools as they now stand.`;
                        })
                      }
                    >
                      Approve this catalog
                    </button>
                  </div>
                )}

                {noteFrom('refresh', 'approve')}
              </td>
            </tr>
          </tbody>
        </table>
      </details>
    </>
  );
}

/**
 * Register a connection from a manifest.
 *
 * The form is the manifest's — reef renders whatever fields the connector
 * declares, and knows nothing about what they mean. No grant is asked for:
 * the API derives the manifest's suggested scope from the configurator, and
 * narrowing beyond that belongs to the mount in the builder.
 */
function AddConnection({ onDone }: { onDone(): void }) {
  const [connectors, setConnectors] = useState<ConnectorManifestSummary[]>([]);
  const [provider, setProvider] = useState<string>('');
  const [id, setId] = useState('');
  // Once somebody edits the id it is theirs; until then it follows the name,
  // so the common case is one field typed rather than two.
  const [idEdited, setIdEdited] = useState(false);
  const [name, setName] = useState('');
  const [values, setValues] = useState<Record<string, string>>({});
  const [issues, setIssues] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState<{ message: string; connectUrl: string | null } | null>(null);

  useEffect(() => {
    api.listConnectors().then((response) => setConnectors(response.connectors)).catch(() => {});
  }, []);

  const manifest = connectors.find((entry) => entry.provider === provider) ?? null;

  useEffect(() => {
    if (!manifest) return;
    setName((current) => current || manifest.name);
  }, [manifest]);

  useEffect(() => {
    if (!idEdited) setId(slugify(name));
  }, [name, idEdited]);

  if (result) {
    return (
      <div className="card">
        <h2>Registered</h2>
        <p>{result.message}</p>
        {result.connectUrl && (
          <p>
            <a className="button primary" href={result.connectUrl}>
              Connect it now
            </a>
          </p>
        )}
        <div className="row">
          <button onClick={onDone}>Back to connections</button>
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <h2>Add a connection</h2>

      <div className="field">
        <label htmlFor="connector">Connector</label>
        <select
          id="connector"
          value={provider}
          onChange={(event) => {
            setProvider(event.target.value);
            setValues({});
            setIssues([]);
          }}
        >
          <option value="">Choose…</option>
          {connectors.map((entry) => (
            <option key={entry.provider} value={entry.provider}>
              {entry.name}
            </option>
          ))}
        </select>
        {manifest && <span className="hint">{manifest.summary}</span>}
      </div>

      {manifest && (
        <>
          <div className="field">
            <label htmlFor="connection-name">Name</label>
            <input
              id="connection-name"
              value={name}
              onChange={(event) => setName(event.target.value)}
            />
          </div>

          <div className="field">
            <label htmlFor="connection-id">Id</label>
            <input
              id="connection-id"
              value={id}
              placeholder={manifest.provider}
              onChange={(event) => {
                setIdEdited(true);
                setId(event.target.value);
              }}
            />
            <span className="hint">What workflows refer to. Short, stable, hard to change later.</span>
          </div>

          {manifest.configurator.map((field) => (
            <div className="field" key={field.key}>
              <label htmlFor={`cfg-${field.key}`}>
                {field.label}
                {field.required ? ' *' : ''}
              </label>
              {field.options ? (
                <select
                  id={`cfg-${field.key}`}
                  value={values[field.key] ?? ''}
                  onChange={(event) =>
                    setValues({ ...values, [field.key]: event.target.value })
                  }
                >
                  <option value="">Choose…</option>
                  {field.options.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  id={`cfg-${field.key}`}
                  value={values[field.key] ?? ''}
                  placeholder={field.placeholder}
                  onChange={(event) =>
                    setValues({ ...values, [field.key]: event.target.value })
                  }
                />
              )}
              {field.help && <span className="hint">{field.help}</span>}
            </div>
          ))}

          {manifest.stagesWrites && (
            <p className="hint">
              {manifest.trustTier === 'byo'
                ? 'Nothing has reviewed what is on the other end of this connector, so every write through it is queued for a human.'
                : 'Writes through this connector are queued for a human rather than performed.'}
            </p>
          )}

          {issues.length > 0 && (
            <div className="banner danger">
              <ul style={{ margin: 0, paddingLeft: '1.2rem' }}>
                {issues.map((issue) => (
                  <li key={issue}>{issue}</li>
                ))}
              </ul>
            </div>
          )}

          <div className="row">
            <button
              className="primary"
              disabled={saving || !id.trim()}
              onClick={async () => {
                setSaving(true);
                setIssues([]);
                try {
                  const response = await api.createConnection({
                    id: id.trim(),
                    name: name.trim() || manifest.name,
                    provider: manifest.provider,
                    configurator: values,
                  });
                  setResult({
                    message:
                      response.toolsDiscovered > 0
                        ? `${response.connection.name} is registered and ${response.toolsDiscovered} tools were discovered.`
                        : response.discoveryError
                          ? `${response.connection.name} is registered, but discovering its tools failed: ${response.discoveryError}`
                          : (response.note ?? `${response.connection.name} is registered.`),
                    connectUrl: response.connectUrl ?? null,
                  });
                } catch (error) {
                  setIssues([(error as Error).message]);
                } finally {
                  setSaving(false);
                }
              }}
            >
              Register
            </button>
            <button disabled={saving} onClick={onDone}>
              Cancel
            </button>
          </div>
        </>
      )}
    </div>
  );
}

function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function IdentityRow({
  kind,
  title,
  tip,
  ready,
  readyText,
  blockedText,
  action,
  extra,
}: {
  kind: 'person' | 'bot';
  title: string;
  tip: string;
  ready: boolean;
  readyText: string;
  blockedText: string;
  action: React.ReactNode;
  extra?: React.ReactNode;
}) {
  return (
    <div className="field">
      <div className="row">
        {/*
          Two signals answering different questions: the badge says *who* this
          is and never changes; the pill says whether it currently works.
        */}
        <IdentityBadge kind={kind} label={title} tip={tip} size="lg" />
        <span className={ready ? 'pill ok' : 'pill warn'}>
          {kind === 'person' ? (ready ? 'Connected' : 'Not connected') : ready ? 'Ready' : 'Not set up'}
        </span>
      </div>
      <span className="hint">{ready ? readyText : blockedText}</span>
      {(action || extra) && (
        <div className="row wrap">
          {action}
          {extra}
        </div>
      )}
    </div>
  );
}

/** Whose credential this is, in one sentence a new reader can act on. */
function describeOwner(connection: ConnectionSummary): string {
  const bot = botLabelFor(connection);
  if (connection.usesUser && connection.usesBot) {
    return `Personal and workspace — steps run as you, with your own login, or as ${bot} when nobody started the run.`;
  }
  if (connection.usesUser) {
    return 'Personal — every step runs as you, with your own login.';
  }
  if (connection.usesBot) {
    return `Workspace service — every step runs with a shared credential an admin sets.`;
  }
  return "Reef's own credentials, never given to a step.";
}

function actorLabel(actor: string, connection: ConnectionSummary): string {
  const bot = connection.serviceAccountLabel ?? 'bot';
  if (actor === 'user') return 'you';
  if (actor === 'service') return bot;
  return connection.usesUser && connection.usesBot ? `you, or ${bot} unattended` : 'default';
}

/**
 * A personal half that is not linked and can be. `needsYou` in the shell is the
 * same filter — the topbar pill counts what this banner offers, so the two must
 * not be allowed to disagree.
 */
function needsYourLink(connection: ConnectionSummary): boolean {
  return connection.usesUser && !connection.userLinked && connection.connectUrl !== null;
}

type OwnerGroup = 'yours' | 'workspace' | 'built-in';

/**
 * Which rail section a connection sits in: by whose credential it resolves to.
 *
 * A dual-identity connection is under **Your accounts**, because the half a
 * reader can do anything about is their own; its bot half is still counted in
 * the topbar's "credentials to set", which is where an admin looks for it.
 */
function ownerFor(connection: ConnectionSummary): OwnerGroup {
  if (connection.usesUser) return 'yours';
  if (connection.usesBot) return 'workspace';
  return 'built-in';
}

/*
 * The first two are drawn even when empty, because their emptiness is an
 * answer: "no personal accounts here" tells a member the screen has nothing
 * for them to do. "Built in" — reef's own loopback servers — is hidden when
 * empty because a deployment registering none is the ordinary case, not a
 * state anybody reached.
 */
const GROUPS: ReadonlyArray<{
  key: OwnerGroup;
  title: string;
  hint: string;
  hideWhenEmpty?: boolean;
}> = [
  {
    key: 'yours',
    title: 'Your accounts',
    hint: 'Connected with your own login. Only you can connect these.',
  },
  {
    key: 'workspace',
    title: 'Workspace services',
    hint: 'A shared credential an admin sets. Everyone uses the same one.',
  },
  {
    key: 'built-in',
    title: 'Built in',
    hint: 'Reef’s own servers. Nothing to set up.',
    hideWhenEmpty: true,
  },
];

/** The sections, alphabetical within each. */
function groupByOwner(
  connections: ConnectionSummary[],
): Array<{ key: OwnerGroup; title: string; hint: string; connections: ConnectionSummary[] }> {
  return GROUPS.map((group) => ({
    ...group,
    connections: connections
      .filter((connection) => ownerFor(connection) === group.key)
      .sort((a, b) => a.name.localeCompare(b.name)),
  })).filter((group) => group.connections.length > 0 || !group.hideWhenEmpty);
}

/**
 * "Used by the factories": the stages whose workflow mounts this connection.
 *
 * Derived from the factory list and the connection's `usedBy` rather than asked
 * of the server, because both are already on the screen and cannot be stale.
 * `usedBy` is scoped to the workflows the caller may see, so a member sees the
 * stages of the lifecycles they are part of.
 */
function FactoryUsage({
  connection,
  factories,
}: {
  connection: ConnectionSummary;
  factories: FactorySummary[];
}) {
  const mounted = new Set(connection.usedBy.map((workflow) => workflow.id));
  const byFactory = factories
    .map((factory) => ({
      factory,
      stages: factory.stages.filter((stage) => mounted.has(stage.workflowId)),
    }))
    .filter((entry) => entry.stages.length > 0);

  return (
    <p className="hint factory-usage" style={{ marginTop: 0 }}>
      <span className="factory-usage-label">Used by the factories:</span>{' '}
      {byFactory.length === 0 ? (
        <span>no stage you can see mounts it.</span>
      ) : (
        byFactory.map((entry, index) => (
          <span key={entry.factory.id}>
            {index > 0 && '; '}
            <strong>{entry.factory.name}</strong> — {entry.stages.map((stage) => stage.name).join(', ')}
          </span>
        ))
      )}
    </p>
  );
}

/**
 * What to do about a missing workspace credential, said to the person who can.
 *
 * An admin gets the exact command; a member gets the secret's name and "ask an
 * administrator" — never the command, because a command they cannot run reads
 * as a step they have failed to do.
 */
function CredentialNeeded({ secretName, isAdmin }: { secretName: string; isAdmin: boolean }) {
  const [copied, setCopied] = useState(false);
  const command = `wrangler secret put ${secretName}`;

  return (
    <div className="credential-needed">
      {isAdmin ? (
        <>
          <p className="hint" style={{ margin: 0 }}>
            Set it on the deployment, or paste the value in Admin → Secrets:
          </p>
          <div className="row" style={{ alignItems: 'stretch' }}>
            <pre className="credential-command">
              <code>{command}</code>
            </pre>
            <button
              className="ghost"
              onClick={() => {
                // The clipboard is absent in an insecure context and the promise
                // rejects; the command is still on screen to copy by hand.
                navigator.clipboard
                  ?.writeText(command)
                  .then(() => setCopied(true))
                  .catch(() => {});
              }}
            >
              {copied ? 'Copied' : 'Copy'}
            </button>
          </div>
        </>
      ) : (
        <p className="hint" style={{ margin: 0 }}>
          This connection needs the workspace secret <code>{secretName}</code>. Ask an
          administrator to set it.
        </p>
      )}
      <p className="hint" style={{ margin: 0 }}>
        Without it, steps that run as the bot stop at this connection.
      </p>
    </div>
  );
}
